import React, { useMemo, useState } from "react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useCreateMasterAccountLocationMutation,
  useListMasterAccountBrandsQuery,
  useListMasterAccountLocationsQuery,
  type ClientLocationRow,
} from "@/features/accounts/accountsApi";
import LocationCreateWorkspace from "@/features/accounts/components/LocationCreateWorkspace";
import type { OperatingHours } from "@/features/accounts/locationCreateHelpers";

interface AccountLocationsListProps {
  masterAccountId: string;
}

/** Operator label — never uses UUID as the primary name. */
export function formatClientLocationLabel(row: ClientLocationRow): {
  location: string;
  brand: string;
} {
  const location = String(row.locationName || "").trim() || "Location";
  const brand = String(row.brandName || "").trim() || "—";
  return { location, brand };
}

/**
 * Client-scoped Locations view.
 * Source of truth: live Location Assignments via GET …/locations
 */
const AccountLocationsList: React.FC<AccountLocationsListProps> = ({
  masterAccountId,
}) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch, error } =
    useListMasterAccountLocationsQuery(masterAccountId, {
      skip: !masterAccountId,
    });
  const { data: clientBrands } = useListMasterAccountBrandsQuery(
    masterAccountId,
    { skip: !masterAccountId },
  );
  const [createLocation] = useCreateMasterAccountLocationMutation();
  const [showCreate, setShowCreate] = useState(false);

  const clientName = data?.businessName || "this Client";
  const locations = data?.locations || [];
  const locationCount =
    typeof data?.locationCount === "number"
      ? data.locationCount
      : locations.length;

  // Human Brand names only — never fall back to brandId UUID.
  const brandOptions = useMemo(() => {
    return (clientBrands?.brands || [])
      .filter(
        (b) =>
          (b.status === "active" ||
            !b.membershipStatus ||
            b.membershipStatus === "active") &&
          String(b.name || "").trim() &&
          String(b.name).trim() !== String(b.brandId),
      )
      .map((b) => ({
        brandId: b.brandId,
        name: String(b.name).trim(),
      }))
      .filter((b) => b.brandId);
  }, [clientBrands]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <PageTitle title={`Locations for ${clientName}`} />
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => setShowCreate(true)}
            data-testid="sa-add-location"
          >
            + Add Location
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/accounts")}
          >
            Back to Clients
          </Button>
        </div>
      </div>

      <p className="text-muted">
        Locations this Client currently operates (via LocationAssignment). Brand
        Location records are not deleted when a Client is archived.
      </p>

      {isLoading && (
        <div className="text-center py-4" data-testid="client-locations-loading">
          <Spinner animation="border" />
        </div>
      )}

      {isError && (
        <Alert variant="danger" data-testid="client-locations-error">
          {(error as { data?: { message?: string } })?.data?.message ||
            "Failed to load locations"}{" "}
          <Button size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      )}

      {!isLoading && !isError && (
        <>
          {locationCount === 0 ? (
            <Alert
              variant="info"
              className="mb-0"
              data-testid="client-locations-empty"
            >
              No locations are currently operated by this Client.
            </Alert>
          ) : (
            <Table
              responsive
              hover
              className="legacy-row-height-table"
              data-testid="client-locations-table"
            >
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Brand</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((row) => {
                  const label = formatClientLocationLabel(row);
                  return (
                    <tr key={row.locationId}>
                      <td>{label.location}</td>
                      <td>{label.brand}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </>
      )}

      <LocationCreateWorkspace
        show={showCreate}
        onHide={() => setShowCreate(false)}
        operatorName={clientName}
        brandOptions={brandOptions}
        onCreated={() => refetch()}
        createLocation={async (args) => {
          const result = await createLocation({
            masterAccountId,
            idempotencyKey: args.idempotencyKey,
            brandId: args.brandId,
            label: args.label,
            storeNumber: args.storeNumber,
            address: args.address,
            timezone: args.timezone,
            operatingHours: args.operatingHours as OperatingHours,
            zones: args.zones,
            confirmDuplicateStoreNumber: args.confirmDuplicateStoreNumber,
          }).unwrap();
          return {
            locationId: result.locationId,
            idempotentReplay: result.idempotentReplay,
          };
        }}
      />
    </>
  );
};

export default AccountLocationsList;
